import { normalizeRotation } from "./imageUtils";

/**
 * 老師端批次上傳「交卷前處理」的編排邏輯（kanban 113663 T7/T8；kanban 114186 稿紙
 * 四角校正改搬進批改後端管線內部處理，前端不再打 /cornerdetect，這支只剩烤旋轉一步）。
 *
 * 新動線：老師只按旋轉鈕，四角校正交給後端在批改過程中自動處理。這支只放
 * 「哪些圖要處理、順序、角度對應，以及每張圖的處理步驟怎麼串」——真正碰 DOM 的
 * bakeRotationIntoImage 由呼叫端注入，所以整份可以在 node 環境單獨測試，不需要 mock canvas。
 */

/** collectBatchPrepTasks / buildBatchStudentFiles 用到的圖片欄位。TFile 泛型讓這份不綁 DOM 的 File。 */
export interface BatchPrepImage<TFile> {
  file: TFile;
  /** 檢視旋轉角度；交卷時才由 bakeRotationIntoImage 烤進像素 */
  rotation?: number;
}

/** 一位學生的一組圖片，imageIndices 依面別排序、指向 uploadedImages。 */
export interface BatchPrepGroup {
  imageIndices: number[];
}

/** 一張待處理的圖：帶著它自己的 index、檔案與角度。 */
export interface BatchPrepTask<TFile> {
  imageIndex: number;
  file: TFile;
  angle: number;
}

/**
 * 把「這次要送出的圖」攤成一張處理清單，同一趟帶出每張圖的旋轉角度。
 *
 * - 只收 groups 真的引用到的圖：沒被引用的不會送出，處理它只是白白多吃一次
 *   大圖 decode + 重編碼的記憶體。
 * - 同一個 imageIndex 只排一次（第一次出現為準）：同一份像素被烤兩遍等於畫質再掉一層。
 * - 角度取自 image 物件本身而非外部平行陣列 —— 頁序前移／後移（moveFace）只調換
 *   imageIndices，刪圖（removeImage）則會讓 index 整排前挪；角度掛在物件上才不會錯位。
 * - imageIndices 指到不存在的圖時整格略過，不推移後面的角度、也不會把 undefined 傳下去。
 */
export function collectBatchPrepTasks<TFile>(
  groups: BatchPrepGroup[],
  images: BatchPrepImage<TFile>[]
): BatchPrepTask<TFile>[] {
  const tasks: BatchPrepTask<TFile>[] = [];
  const seen = new Set<number>();
  for (const group of groups || []) {
    for (const imageIndex of group?.imageIndices ?? []) {
      const image = images?.[imageIndex];
      if (!image || seen.has(imageIndex)) continue;
      seen.add(imageIndex);
      tasks.push({
        imageIndex,
        file: image.file,
        angle: normalizeRotation(image.rotation ?? 0),
      });
    }
  }
  return tasks;
}

/** prepareBatchFiles 的處理步驟；呼叫端注入真正碰 DOM 的實作。 */
export interface BatchPrepSteps<TFile> {
  /** 把旋轉角度烤進像素（bakeRotationIntoImage）。角度 0 時應原檔直送、不重編碼。 */
  bake: (file: TFile, angle: number) => Promise<TFile> | TFile;
}

/**
 * 逐張把待處理清單跑完，回傳 imageIndex → 處理後檔案。
 *
 * ⚠️ 逐張 await，不可改成 Promise.all：多張大圖同時 decode + 重編碼會吃爆手機記憶體。
 * 批次是整班多位學生 × 多面，風險比單篇高得多。
 *
 * 每張做完回報一次進度 —— 整班數十張要跑好一陣子，沒有提示老師會以為當掉而重按送出。
 */
export async function prepareBatchFiles<TFile>(
  tasks: BatchPrepTask<TFile>[],
  steps: BatchPrepSteps<TFile>,
  onProgress?: (done: number, total: number) => void
): Promise<Map<number, TFile>> {
  const prepared = new Map<number, TFile>();
  const list = tasks || [];
  const total = list.length;
  for (let i = 0; i < total; i++) {
    const task = list[i];
    const file = await steps.bake(task.file, task.angle);
    prepared.set(task.imageIndex, file);
    onProgress?.(i + 1, total);
  }
  return prepared;
}

/**
 * 把處理後的檔案塞回每位學生的送出清單。
 *
 * - 一個 group 一格、順序照 groups；group 內順序照 imageIndices（= 老師確認過的面別順序）。
 * - 查不到處理結果時 fail-open 用原檔：寧可送未處理的圖，也不要送出 undefined 讓整批爛掉。
 * - 無效 index 略過，不在檔案陣列裡留下空洞。
 */
export function buildBatchStudentFiles<TFile>(
  groups: BatchPrepGroup[],
  images: BatchPrepImage<TFile>[],
  prepared: Map<number, TFile>
): TFile[][] {
  return (groups || []).map((group) => {
    const files: TFile[] = [];
    for (const imageIndex of group?.imageIndices ?? []) {
      const image = images?.[imageIndex];
      if (!image) continue;
      files.push(prepared?.get(imageIndex) ?? image.file);
    }
    return files;
  });
}
